/**
 * pending.js
 */

import React from 'react';
import {
  useMediaQuery,
  IconButton,
  List
} from '@mui/material';
import { parseISO, format } from 'date-fns';
import { UserContext } from 'contexts/user/user-context';
import Icon from 'components/icon';
import DropMenu from 'components/drop-menu';
import DataGrid from 'components/data-grid';
import AcceptButton from 'components/groups/accept-button';
import RequestType from 'routing/routes/config/groups/request-type';
import NameCell from './name-cell';
import RowMobile from './row-mobile';
import ConfirmRequestDialog from './confirm-request-dialog';



export default function Pending(props) {
  const {
    requests,
    onRemove,
    onAccept
  } = props;
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { user } = React.useContext(UserContext);
  const [confirmRequest, setConfirmRequest] = React.useState();

  // supervisors can receive requests to manage or be managed so we have to determine which
  // user to display (manager or managee)
  const parsedRequests = React.useMemo(() => requests.map(r => ({
    ...r,
    userDisplay: r.user.id === user.id ? r.manager : r.user
  })), [requests, user]);


  const pendingMenu = (memberRequest) => {
    const items = [];
    if (memberRequest.creator.id !== user.id) {
      items.push({
        id: 'accept',
        icon: 'group-accept',
        name: 'Accept',
        onSelect: () => setConfirmRequest(memberRequest)
      });
    }
    items.push({
      id: 'remove',
      icon: 'delete',
      name: 'Remove ...',
      onSelect: () => onRemove(memberRequest.code, memberRequest.user.id, {
        userType: 'Request',
        text: `remove ${memberRequest.user.name} from your list of pending requests`
      })
    });

    return (<>
      {memberRequest.creator.id !== user.id
        ? <AcceptButton onClick={() => setConfirmRequest(memberRequest)} />
        : null}
      <DropMenu
        id={`${memberRequest.id}-pending-menu`}
        label={<IconButton size='small'><Icon type='menu' /></IconButton>}
        items={items}
      />
    </>);
  }


  const columnDefs = [{ 
    field: 'name',
    headerName: 'Name',
    width: 300,
    valueGetter: (params) => params.row.userDisplay.name,
    renderCell: (params) => (<>
      <NameCell name={params.row.userDisplay.name} department={params.row.userDisplay.departmentName} />
      <RequestType context='manage' request={params.row} />
    </>),
  }, {
    field: 'email',
    headerName: 'Email',
    width: 200,
    valueGetter: params => params.row.userDisplay.email
  }, {
    field: 'role',
    headerName: 'Request Type',
    width: 125,
    cellClassName: 'request-type-cell',
    valueGetter: params => params.value.name,
    renderCell: params => <>{params.value}<br/>Access</>
  }, {
    field: 'dateCreated',
    headerName: 'Sent On',
    width: 120,
    valueGetter: params => format(parseISO(params.row.dateCreated), 'MM/dd/yyyy')
  }, {
    field: 'menu',
    headerName: ' ',
    sortable: false,
    align: 'right',
    width: 95,
    minWidth: 95,
    renderCell: (params) => pendingMenu(params.row),
  }];


  return (
    <>
      {/* MOBILE */}
      {isMobile && 
      <List>
        {requests.map((pender, i) => <RowMobile
          key={i}
          nameContent={<>
            <NameCell name={pender.user.name} department={pender.user.departmentName} />
            <RequestType context='manage' request={pender} />
          </>}
          menu={pendingMenu(pender)}
        />)}
      </List>}

      {/* DESKTOP */}
      {!isMobile &&
      <DataGrid
        rows={parsedRequests}
        columns={columnDefs}
        headerHeight={48}
        rowHeight={48}
        autoHeight
        disableColumnMenu
        disableRowSelectionOnClick
        hideFooter
        hideFooterSelectedRowCount
      />}

      {/* confirm request dialog */}
      <ConfirmRequestDialog
        isOpen={Boolean(confirmRequest)}
        onClose={() => setConfirmRequest()}
        onOk={() => {
          setConfirmRequest();
          onAccept(confirmRequest);
        }}
        request={confirmRequest}
      />
    </>
  );
}